import { globalConfirm } from '../components/ConfirmDialog';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { LoadingSpinner, EmptyState } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import TypeAheadLookup from '../components/TypeAheadLookup';
import { useAuth } from '../contexts/AuthContext';
import { useExpenditureLabels, useDecimalFormat } from '../contexts/ExpenditureLabelsContext';
import { formatTimestamp as fmtTs } from './Requisitions/constants';
import api, { getApiErrorMessage } from '../services/api';
import { toast } from 'sonner';
import { AttachmentsPanel, TermsPanel, NotesPanel, CostCentreSplitsPanel } from '../components/shared/DocumentPanels';
import GoodsReceivedPanel from './PurchaseOrders/GoodsReceivedPanel';
import { SiteSelector } from '../components/SiteSelector';
import { SortableTableHeader, useSortState, sortData } from '../components/SortableTableHeader';
import { POLineItemsPanel } from './PurchaseOrders/POLineItemsPanel';
import { POInvoicesPanel } from './PurchaseOrders/POInvoicesPanel';
import { POApprovalRulesPanel, POApproversPanel, DetailInfoCard } from './PurchaseOrders/POApprovalPanels';
import {
  ChevronDown, ChevronLeft, ChevronRight, X, Save, Upload, ChevronsDown,
  Layers, FileText, CheckSquare, Split, Paperclip, ScrollText,
  StickyNote, Activity, Users, Package, BarChart2,
  Download, Eye, History, ExternalLink, RefreshCw, Receipt, ShoppingCart
} from 'lucide-react';

const EDITABLE = new Set(['DRAFT', 'Draft', 'UNISSUED', 'Unissued', 'AMENDED', 'Amended', 'APPROVED', 'Approved', 'ISSUED', 'Issued', 'PARTIAL', 'Partial']);
const READ_ONLY = new Set(['CANCELLED', 'Cancelled', 'CLOSED', 'Closed', 'COMPLETED', 'Completed']);

const PO_PANELS = [
  { id: 'line-items',     icon: Package,      label: 'Line Items' },
  { id: 'rule',           icon: FileText,      label: 'Rule' },
  { id: 'rule-approvers', icon: CheckSquare,   label: 'Rule Approvers' },
  { id: 'cc-splits',      icon: Split,         label: 'Cost Centre Splits' },
  { id: 'cc-approval',    icon: Users,         label: 'Cost Centre Approval' },
  { id: 'group-approvers',icon: Users,         label: 'Group Approvers' },
  { id: 'attachments',    icon: Paperclip,     label: 'Attached Documents' },
  { id: 'terms',          icon: ScrollText,    label: 'Terms & Conditions' },
  { id: 'goods-received', icon: Download,     label: 'Receiving' },
  { id: 'invoices',       icon: Receipt,       label: 'Invoices' },
  { id: 'notes',          icon: StickyNote,    label: 'Notes' },
  { id: 'events',         icon: Activity,      label: 'Event Log' },
];

const STATUS_COLORS = {
  UNISSUED: 'bg-amber-100 text-amber-700 border-amber-200',
  PENDING: 'bg-purple-100 text-purple-700 border-purple-200',
  APPROVED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  ISSUED: 'bg-blue-100 text-blue-700 border-blue-200',
  ACKNOWLEDGED: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  AMENDED: 'bg-orange-100 text-orange-700 border-orange-200',
  RECEIVED: 'bg-teal-100 text-teal-700 border-teal-200',
  PARTIAL: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  COMPLETED: 'bg-green-100 text-green-700 border-green-200',
  CANCELLED: 'bg-red-100 text-red-700 border-red-200',
  CLOSED: 'bg-slate-100 text-slate-700 border-slate-200',
};

export default function PurchaseOrderDetailPage() {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const { user, systemSettings } = useAuth();
  const { labels: expLabels } = useExpenditureLabels();
  const { fmtVal, fmtTotal } = useDecimalFormat();
  const tz = systemSettings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const fmtTz = (d) => fmtTs(d, tz);
  const currency = systemSettings?.system_currency || 'USD';

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePanel, setActivePanel] = useState('line-items');
  const [headerEdit, setHeaderEdit] = useState({});
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState([]);
  const [notes, setNotes] = useState([]);
  const [poEvtSort, handlePoEvtSort] = useSortState('event_date', 'desc');
  const [poGrnSort, handlePoGrnSort] = useSortState('', 'asc');
  const [supplierInfo, setSupplierInfo] = useState(null);
  const [fieldOptions, setFieldOptions] = useState({ shipping_methods: [], incoterms: [] });
  const [supplierContacts, setSupplierContacts] = useState([]);
  const [supplierContracts, setSupplierContracts] = useState([]);
  const [mandatoryFields, setMandatoryFields] = useState({ header: [], line: [] });
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showHeaderSend, setShowHeaderSend] = useState(false);
  const [headerSending, setHeaderSending] = useState(false);
  const [headerSupplierEmail, setHeaderSupplierEmail] = useState('');

  const canEdit = order && !READ_ONLY.has(order.status);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/orders/${orderNumber}`);
      setOrder(r.data);
      setHeaderEdit(r.data);
      if (r.data.supplier_code) {
        api.get(`/api/data/suppliers/${r.data.supplier_code}`).then(sr => {
          setSupplierInfo(sr.data);
          setSupplierContacts(sr.data?.contacts || []);
          // Default payment terms from supplier if not already set on order
          if (!r.data.payment_terms && sr.data?.financial_details?.payment_terms) {
            setHeaderEdit(prev => ({ ...prev, payment_terms: sr.data.financial_details.payment_terms }));
          }
        }).catch(() => {});
        api.get(`/api/orders/supplier-contracts/${r.data.supplier_code}`).then(cr => setSupplierContracts(cr.data || [])).catch(() => {});
      }
    } catch (e) { if (e?.response?.status >= 500) toast.error('Failed to load order'); }
    setLoading(false);
  }, [orderNumber]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/api/orders/field-options').then(r => setFieldOptions(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get('/api/admin/order-mandatory-fields').then(r => setMandatoryFields(r.data.mandatory_fields || { header: [], line: [] })).catch(() => {}); }, []);

  useEffect(() => {
    if (activePanel === 'events') {
      api.get(`/api/orders/${orderNumber}/events`).then(r => setEvents(r.data || [])).catch(() => {});
    }
    if (activePanel === 'notes') {
      api.get(`/api/orders/${orderNumber}/notes`).then(r => setNotes(r.data || [])).catch(() => {});
    }
  }, [activePanel, orderNumber]);

  const h = (k, v) => setHeaderEdit(p => ({ ...p, [k]: v }));
  const isMandatory = (field) => mandatoryFields.header?.includes(field);
  const isLineMandatory = (field) => mandatoryFields.line?.includes(field);
  const mandatoryLabel = (field, label) => (
    <p className={`text-xs font-medium ${isMandatory(field) ? 'text-red-500' : 'text-muted-foreground'}`}>{label}{isMandatory(field) ? ' *' : ''}</p>
  );

  const COMMON_PAYMENT_TERMS = ['Net 30', 'Net 60', 'Net 90', 'Net 15', 'Due on Receipt', 'Prepaid', '2/10 Net 30', 'COD', 'Annual', 'Monthly'];

  const onSupplierChange = (code, rec) => {
    h('supplier_code', code);
    if (rec) h('supplier_name', rec.supplier_name);
    if (code) {
      api.get(`/api/data/suppliers/${code}`).then(sr => {
        setSupplierInfo(sr.data);
        const contacts = sr.data?.contacts || [];
        setSupplierContacts(contacts);
        const primary = contacts.find(c => c.is_main);
        if (primary) {
          h('supplier_contact', primary.name ? `${primary.name}${primary.email ? ` (${primary.email})` : ''}` : primary.email || '');
        } else if (contacts.length === 1) {
          const c = contacts[0];
          h('supplier_contact', c.name ? `${c.name}${c.email ? ` (${c.email})` : ''}` : c.email || '');
        }
        if (sr.data?.financial_details?.payment_terms) {
          h('payment_terms', sr.data.financial_details.payment_terms);
        }
      }).catch(() => {});
      api.get(`/api/orders/supplier-contracts/${code}`).then(cr => setSupplierContracts(cr.data || [])).catch(() => {});
    } else {
      setSupplierContacts([]);
      setSupplierContracts([]);
    }
  };

  const saveHeader = async () => {
    // Supplier code is always mandatory
    if (!headerEdit.supplier_code) {
      toast.error('Supplier Code is required');
      return;
    }
    // Validate configurable mandatory fields
    const missing = [];
    const fieldLabels = { description: 'Description', delivery_to: 'Delivery To', delivery_code: 'Delivery Code', document_type: 'Document Type', category_code: 'Category Code', instructions: 'Instructions', incoterms: 'Incoterms', ship_via: 'Ship Via', account: expLabels.exp_code_1 || 'Account', restaurant: expLabels.exp_code_2 || 'Restaurant', department_code: expLabels.exp_code_3 || 'Department Code', payment_terms: 'Payment Terms', supplier_code: 'Supplier' };
    for (const field of mandatoryFields.header || []) {
      if (field === 'budget') continue; // Budget is not mandatory
      if (!headerEdit[field]) missing.push(fieldLabels[field] || field);
    }
    if (missing.length > 0) {
      toast.error(`Missing mandatory fields: ${missing.join(', ')}`);
      return;
    }
    setSaving(true);
    try {
      const { id, _id, order_number, created_at, updated_at, line_items, ...payload } = headerEdit;
      await api.put(`/api/orders/${orderNumber}`, payload);
      toast.success('Order saved');
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Save failed')); }
    setSaving(false);
  };

  const [commandMenuOpen, setCommandMenuOpen] = useState(false);
  const [detailPopover, setDetailPopover] = useState(null); // 'originator'|'buyer'|'supplier'|'delivery'|'invoice'
  const [currencyDialog, setCurrencyDialog] = useState(false);
  const [newCurrency, setNewCurrency] = useState(order?.currency || 'USD');
  const [templateName, setTemplateName] = useState('');
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versionData, setVersionData] = useState(null);
  const [loadingVersions, setLoadingVersions] = useState(false);

  const openVersionHistory = async () => {
    setShowVersionHistory(true);
    setLoadingVersions(true);
    try {
      const r = await api.get(`/api/orders/${orderNumber}/versions`);
      setVersionData(r.data);
    } catch (e) { if (e?.response?.status >= 500) toast.error('Failed to load version history'); }
    setLoadingVersions(false);
  };

  if (loading) return <AppShell><LoadingSpinner /></AppShell>;
  if (!order) return <AppShell><EmptyState title="Order not found" /></AppShell>;

  const statusCls = STATUS_COLORS[order.status?.toUpperCase()] || STATUS_COLORS.DRAFT;

  const copyOrder = async () => {
    try {
      const r = await api.post(`/api/orders/${orderNumber}/copy`);
      toast.success(`Copied to ${r.data.order_number}`);
      navigate(`/orders/${r.data.order_number}`);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Copy failed')); }
  };


  const cancelOrder = async () => {
    if (!cancelReason.trim()) { toast.error('Cancellation reason is required'); return; }
    try {
      await api.post(`/api/orders/${orderNumber}/status`, { status: 'CANCELLED', notes: cancelReason.trim() });
      toast.success('Order cancelled');
      setShowCancelDialog(false);
      setCancelReason('');
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Cancel failed')); }
  };

  const changeCurrency = async () => {
    try {
      const r = await api.post(`/api/orders/${orderNumber}/change-currency`, { currency: newCurrency });
      toast.success(r.data.message);
      setCurrencyDialog(false);
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
  };

  const openHeaderSend = async () => {
    setShowHeaderSend(true);
    try {
      if (order.supplier_code) {
        const r = await api.get(`/api/data/suppliers/${order.supplier_code}`);
        const main = (r.data?.contacts || []).find(c => c.is_main && c.email);
        setHeaderSupplierEmail(main?.email || '');
      } else { setHeaderSupplierEmail(''); }
    } catch { setHeaderSupplierEmail(''); }
  };

  const confirmHeaderSend = async () => {
    setHeaderSending(true);
    try {
      const r = await api.post(`/api/orders/${orderNumber}/send-to-supplier`);
      toast.success(r.data.message);
      setShowHeaderSend(false);
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Send failed')); }
    setHeaderSending(false);
  };

  const isCompleted = order && order.status?.toUpperCase() === 'COMPLETED';
  const isIssued = order && ['ISSUED', 'PARTIAL'].includes(order.status?.toUpperCase());
  const canSendFromHeader = order && ['UNISSUED', 'APPROVED'].includes(order.status?.toUpperCase());

  const saveTemplate = async () => {
    try {
      const r = await api.post(`/api/orders/${orderNumber}/save-template`, { template_name: templateName || `Template from ${orderNumber}` });
      toast.success(r.data.message);
      setShowTemplateDialog(false);
      setTemplateName('');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
  };

  return (
    <AppShell>
      {/* Top bar — matching the screenshot */}
      <div className="bg-card border rounded-lg p-3 mb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {/* Command Menu */}
            <div className="relative">
              <button onClick={() => setCommandMenuOpen(!commandMenuOpen)} className="flex items-center gap-1 font-bold text-primary hover:underline">
                <ChevronsDown className="h-5 w-5 text-orange-500" strokeWidth={3} />
                Order
              </button>
              {commandMenuOpen && (
                <div className="absolute left-0 top-full mt-1 bg-card border rounded-xl shadow-2xl z-50 p-4 w-[520px]" onClick={e => e.stopPropagation()}>
                  <div className="grid grid-cols-3 gap-6">
                    {/* Menu Column */}
                    <div>
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Menu</p>
                      {['Originator Details', 'Buyer Details', 'Supplier Details', 'Delivery Point Details', 'Invoice Point Details'].map(item => (
                        <button key={item} className="w-full text-left text-sm py-1 hover:text-primary transition-colors" onClick={() => { setDetailPopover(item.split(' ')[0].toLowerCase()); setCommandMenuOpen(false); }}>{item}</button>
                      ))}
                    </div>
                    {/* Actions Column */}
                    <div>
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Actions</p>
                      <button className="w-full text-left text-sm py-1 hover:text-primary" onClick={() => { copyOrder(); setCommandMenuOpen(false); }}>Copy Order</button>
                      {['UNISSUED','ISSUED','PENDING','APPROVED','AMENDED'].includes(order.status?.toUpperCase()) && (
                        <button className="w-full text-left text-sm py-1 hover:text-red-600" onClick={() => { setShowCancelDialog(true); setCommandMenuOpen(false); }}>Cancel Order</button>
                      )}
                      {!['CANCELLED','CLOSED','RECEIVED','PARTIAL','COMPLETED','PENDING'].includes(order.status?.toUpperCase()) && (
                        <button className="w-full text-left text-sm py-1 hover:text-primary" onClick={() => { setCurrencyDialog(true); setCommandMenuOpen(false); }}>Change Order Currency</button>
                      )}
                      <button className="w-full text-left text-sm py-1 hover:text-primary" onClick={() => { setShowTemplateDialog(true); setCommandMenuOpen(false); }}>Save as Template</button>
                    </div>
                    {/* Export & Reports Column */}
                    <div>
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">View / Print</p>
                      <button className="w-full text-left text-sm py-1 hover:text-primary" onClick={() => { window.open(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderNumber}/report/html?internal=true`, '_blank'); setCommandMenuOpen(false); }}>View Internal Order</button>
                      <button className="w-full text-left text-sm py-1 hover:text-primary" onClick={() => { openVersionHistory(); setCommandMenuOpen(false); }}>View External Order</button>
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2 mt-3">Export As</p>
                      <button className="w-full text-left text-sm py-1 hover:text-primary" onClick={() => { window.open(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderNumber}/report/pdf?internal=true`, '_blank'); setCommandMenuOpen(false); }}>PDF</button>
                      <button className="w-full text-left text-sm py-1 hover:text-primary" onClick={() => { window.open(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderNumber}/export-excel`, '_blank'); setCommandMenuOpen(false); }}>Excel Spreadsheet</button>
                      <button className="w-full text-left text-sm py-1 hover:text-primary" onClick={() => { window.open(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderNumber}/report/docx`, '_blank'); setCommandMenuOpen(false); }}>Word Document</button>
                      <button className="w-full text-left text-sm py-1 hover:text-primary" onClick={() => { window.open(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderNumber}/report/rtf`, '_blank'); setCommandMenuOpen(false); }}>RTF Document</button>
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2 mt-3">Send</p>
                      <button className="w-full text-left text-sm py-1 hover:text-primary" onClick={() => { const email = window.prompt('Email address:'); if (email) { api.post(`/api/orders/${orderNumber}/report/email`, { email, internal: true }).then(() => toast.success(`Sent to ${email}`)).catch(e => toast.error(getApiErrorMessage(e, 'Send failed'))); } setCommandMenuOpen(false); }}>Email to User</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {order.version > 1 && <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-700 border-orange-200">Amendment {order.version - 1}</Badge>}
          </div>
          <div className="flex items-center gap-1">
            {supplierInfo?.punchout_enabled && (
              <Button size="sm" variant="outline" className="text-purple-600 border-purple-300 hover:bg-purple-50" onClick={() => {
                const url = supplierInfo.punchout_url || supplierInfo.website;
                if (url) window.open(url, '_blank');
                else toast.info('No punchout URL configured for this supplier');
              }}>
                <ShoppingCart className="h-3.5 w-3.5 mr-1" /> Punchout to Supplier
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => navigate(-1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" onClick={() => navigate('/orders')}><X className="h-4 w-4 text-red-500" /></Button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-3 text-xs">
          <div><p className="text-muted-foreground">Order Number</p><p className="font-bold text-sm">{order.order_number}</p></div>
          <div><p className="text-muted-foreground">Order Status</p><Badge className={`${statusCls} text-xs mt-0.5`}>{order.status}</Badge></div>
          <div><p className="text-muted-foreground">Order Date</p><p className="font-medium">{order.order_date ? new Date(order.order_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : '—'}</p></div>
          <div><p className="text-muted-foreground">Rule Approval</p><p className="font-medium">{order.rule_approval || 'OPTIONAL'}</p></div>
          <div><p className="text-muted-foreground">Cost Centre Approval</p><p className="font-medium">{order.cc_approval || 'OPTIONAL'}</p></div>
          <div><p className="text-muted-foreground">Group Approval</p><p className="font-medium">{order.group_approval || 'OPTIONAL'}</p></div>
          <div><p className="text-muted-foreground">Request Number</p>{order.request_number ? <a href={`/requests/${order.request_number}`} className="text-primary font-bold hover:underline">{order.request_number}</a> : <p>—</p>}</div>
          {order.is_urgent && <div><Badge className="bg-red-600 text-white text-xs animate-pulse">URGENT</Badge></div>}
        </div>
      </div>

      {/* Order Header — collapsible */}
      <div className="bg-card border rounded-lg mb-3">
        <button onClick={() => setHeaderCollapsed(!headerCollapsed)} className="w-full flex items-center justify-between px-4 py-2 hover:bg-accent/30">
          <span className="flex items-center gap-2 text-sm font-semibold">
            <ChevronDown className={`h-4 w-4 transition-transform ${headerCollapsed ? '-rotate-90' : ''}`} /> Order Header
            {(headerEdit.is_urgent || order.is_urgent) && <span className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-0.5 font-bold">URGENT</span>}
          </span>
          <span className="flex items-center gap-3">
            {canEdit && (
              <label className="flex items-center gap-1.5 cursor-pointer" onClick={e => e.stopPropagation()}>
                <input type="checkbox" checked={!!headerEdit.is_urgent} onChange={e => h('is_urgent', e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-red-300 text-red-600 focus:ring-red-500" />
                <span className={`text-xs font-semibold ${headerEdit.is_urgent ? 'text-red-600' : 'text-muted-foreground'}`}>Urgent</span>
              </label>
            )}
            <span className="text-lg font-bold text-primary">Order Net Total {fmtTotal(order.net_total || order.total_value || 0)} <span className="text-xs font-normal text-muted-foreground">({currency})</span></span>
          </span>
        </button>
        {!headerCollapsed && (
          <div className="px-4 pb-4 pt-2">
            <div className="grid grid-cols-6 gap-x-3 gap-y-3 text-sm items-start">
              {/* Row 1 */}
              <div className="space-y-1">{mandatoryLabel('ref_num', 'Ref Num')}
                {canEdit ? <Input value={headerEdit.ref_num||''} onChange={e=>h('ref_num',e.target.value)} className="h-8 text-sm" /> : <p className="h-8 flex items-center text-sm">{order.ref_num||'—'}</p>}</div>
              <div className="space-y-1"><Label className="text-xs text-muted-foreground">&nbsp;</Label>
                {canEdit ? (
                  <label className="flex items-center gap-1.5 h-8 cursor-pointer px-2 rounded-lg border border-dashed border-muted hover:border-purple-300">
                    <input type="checkbox" checked={!!headerEdit.is_punchout} onChange={e => h('is_punchout', e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-purple-400 text-purple-600 focus:ring-purple-500" />
                    <span className={`text-xs font-semibold whitespace-nowrap ${headerEdit.is_punchout ? 'text-purple-600' : 'text-muted-foreground'}`}>Punchout</span>
                  </label>
                ) : <div className="h-8 flex items-center">{order.is_punchout && <span className="text-xs text-purple-600 font-semibold">Punchout</span>}</div>}</div>
              <div className="space-y-1"><p className="text-xs font-medium text-red-500">Supplier Code *</p>
                {canEdit ? <TypeAheadLookup endpoint={headerEdit.is_punchout ? "/api/data/suppliers?punchout=true" : "/api/data/suppliers"} valueField="supplier_code" labelField="supplier_name" value={headerEdit.supplier_code||''} displayValue={headerEdit.supplier_code||''} placeholder={headerEdit.is_punchout ? "Punchout vendors..." : "Search..."} onChange={onSupplierChange} /> : <p className="h-8 flex items-center text-sm">{order.supplier_code||'—'}</p>}</div>
              <div className="space-y-1"><Label className="text-xs text-muted-foreground">Supplier Name</Label>
                <p className="h-8 flex items-center text-sm truncate">{headerEdit.supplier_name || order.supplier_name || '—'}</p></div>
              <div className="space-y-1">{mandatoryLabel('invoice_code', 'Invoice Code')}
                {canEdit ? <TypeAheadLookup endpoint="/api/data/invoice-points" valueField="code" labelField="name" value={headerEdit.invoice_code||''} displayValue={headerEdit.invoice_code||''} onChange={code=>h('invoice_code',code)} /> : <p className="h-8 flex items-center text-sm">{order.invoice_code||'—'}</p>}</div>
              <div className="space-y-1"><Label className="text-xs text-muted-foreground">Budget</Label>
                {canEdit ? <select value={headerEdit.budget_status||'IN_BUDGET'} onChange={e=>h('budget_status',e.target.value)} className={`h-8 w-full rounded-lg border px-2 text-sm font-semibold ${headerEdit.budget_status === 'OVER_BUDGET' ? 'border-red-300 bg-red-50 text-red-700' : headerEdit.budget_status === 'OUT_OF_BUDGET' ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-green-300 bg-green-50 text-green-700'}`}>
                  <option value="IN_BUDGET">In Budget</option><option value="OUT_OF_BUDGET">Out of Budget</option><option value="OVER_BUDGET">Over Budget</option>
                </select> : <p className={order.budget_status === 'OVER_BUDGET' ? 'text-red-600' : 'text-green-600'}>{order.budget_status === 'IN_BUDGET' ? 'In Budget' : order.budget_status === 'OUT_OF_BUDGET' ? 'Out of Budget' : 'Over Budget'}</p>}</div>

              {/* Row 2: Three expense codes */}
              <div className="col-span-2 space-y-1">{mandatoryLabel('account', expLabels.exp_code_1 || 'Account')}
                {canEdit ? <TypeAheadLookup endpoint="/api/account-codes/1" valueField="code" labelField="description" value={headerEdit.account||''} displayValue={headerEdit.account||''} onChange={code=>h('account',code)} /> : <p>{order.account||'—'}</p>}</div>
              <div className="col-span-2 space-y-1">{mandatoryLabel('restaurant', expLabels.exp_code_2 || 'Restaurant')}
                {canEdit ? <TypeAheadLookup endpoint="/api/account-codes/2" valueField="code" labelField="description" value={headerEdit.restaurant||''} displayValue={headerEdit.restaurant||''} onChange={code=>h('restaurant',code)} /> : <p>{order.restaurant||'—'}</p>}</div>
              <div className="col-span-2 space-y-1">{mandatoryLabel('department_code', expLabels.exp_code_3 || 'Department Code')}
                {canEdit ? <TypeAheadLookup endpoint="/api/account-codes/3" valueField="code" labelField="description" value={headerEdit.department_code||''} displayValue={headerEdit.department_code||''} onChange={code=>h('department_code',code)} /> : <p className="h-8 flex items-center text-sm">{order.department_code||'—'}</p>}</div>

              {/* Row 3 */}
              <div className="space-y-1"><p className="text-xs font-medium text-muted-foreground">Delivery Code</p>
                {canEdit ? <TypeAheadLookup endpoint="/api/data/delivery-points" valueField="code" labelField="name" value={headerEdit.delivery_code||''} displayValue={headerEdit.delivery_code||''} onChange={(code,rec)=>{h('delivery_code',code); if(rec){h('delivery_to', rec.contact_name || ''); if(rec.instructions) h('instructions',rec.instructions);}}} /> : <p className="h-8 flex items-center text-sm">{order.delivery_code||'—'}</p>}</div>
              <div className="space-y-1"><p className="text-xs font-medium text-muted-foreground">Delivery To</p>
                {canEdit ? <Input value={headerEdit.delivery_to||''} onChange={e=>h('delivery_to',e.target.value)} className="h-8 text-sm" /> : <p className="h-8 flex items-center text-sm">{order.delivery_to||'—'}</p>}</div>
              <div className="space-y-1"><p className="text-xs font-medium text-muted-foreground">Category Code</p>
                {canEdit ? <TypeAheadLookup endpoint="/api/data/categories" valueField="code" labelField="description" value={headerEdit.category_code||''} displayValue={headerEdit.category_code||''} onChange={code=>h('category_code',code)} /> : <p className="h-8 flex items-center text-sm">{order.category_code||'—'}</p>}</div>
              <div className="space-y-1"><p className="text-xs font-medium text-muted-foreground">Description</p>
                {canEdit ? <Input value={headerEdit.description||''} onChange={e=>h('description',e.target.value)} className="h-8 text-sm" /> : <p className="h-8 flex items-center text-sm truncate">{order.description||'—'}</p>}</div>
              <div className="space-y-1"><p className="text-xs font-medium text-muted-foreground">Instructions</p>
                {canEdit ? <Input value={headerEdit.instructions||''} onChange={e=>h('instructions',e.target.value)} className="h-8 text-sm" /> : <p className="h-8 flex items-center text-sm truncate">{order.instructions||'—'}</p>}</div>
              <div className="space-y-1"><p className="text-xs font-medium text-muted-foreground">Document Type</p>
                {canEdit ? <TypeAheadLookup endpoint="/api/data/document-types" valueField="code" labelField="description" value={headerEdit.document_type||''} displayValue={headerEdit.document_type||''} onChange={code=>h('document_type',code)} /> : <p className="h-8 flex items-center text-sm">{order.document_type||'—'}</p>}</div>

              {/* Row 4 */}
              <div className="space-y-1"><p className="text-xs font-medium text-muted-foreground">Supplier Contact</p>
                {canEdit ? (
                  supplierContacts.length > 1 ? (
                    <select value={headerEdit.supplier_contact||''} onChange={e=>h('supplier_contact',e.target.value)}
                      className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm">
                      <option value="">Select contact...</option>
                      {supplierContacts.map((c, i) => {
                        const val = c.name ? `${c.name}${c.email ? ` (${c.email})` : ''}` : c.email || '';
                        return <option key={i} value={val}>{val}{c.is_main ? ' (Primary)' : ''}</option>;
                      })}
                    </select>
                  ) : (
                    <Input value={headerEdit.supplier_contact||''} onChange={e=>h('supplier_contact',e.target.value)} className="h-8 text-sm" readOnly={supplierContacts.length === 1} />
                  )
                ) : <p className="h-8 flex items-center text-sm">{order.supplier_contact||'—'}</p>}</div>
              <div className="space-y-1"><p className="text-xs font-medium text-muted-foreground">Shipping Method</p>
                {canEdit ? (
                  <select value={headerEdit.shipping_method||''} onChange={e=>h('shipping_method',e.target.value)} className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm">
                    <option value="">Select...</option>
                    {fieldOptions.shipping_methods.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                ) : <p className="h-8 flex items-center text-sm">{order.shipping_method||'—'}</p>}</div>
              <div className="space-y-1"><p className="text-xs font-medium text-muted-foreground">Payment Terms</p>
                {canEdit ? (
                  <div className="relative">
                    <Input value={headerEdit.payment_terms||''} onChange={e=>h('payment_terms',e.target.value)} className="h-8 text-sm" placeholder="Type or select..." list="payment-terms-list" />
                    <datalist id="payment-terms-list">
                      {COMMON_PAYMENT_TERMS.map(t => <option key={t} value={t} />)}
                    </datalist>
                  </div>
                ) : <p className="h-8 flex items-center text-sm">{order.payment_terms||'—'}</p>}</div>
              <div className="space-y-1"><p className="text-xs font-medium text-muted-foreground">Incoterms</p>
                {canEdit ? (
                  <select value={headerEdit.incoterms||''} onChange={e=>h('incoterms',e.target.value)} className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm">
                    <option value="">Select...</option>
                    {fieldOptions.incoterms.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                ) : <p className="h-8 flex items-center text-sm">{order.incoterms||'—'}</p>}</div>
              <div className="space-y-1"><p className="text-xs font-medium text-muted-foreground">Contract Number</p>
                {canEdit ? (
                  <div className="relative">
                    <Input value={headerEdit.contract_number||''} onChange={e=>h('contract_number',e.target.value)} className="h-8 text-sm" placeholder="Type or select..." list={supplierContracts.length > 0 ? 'supplier-contracts-list' : undefined} />
                    {supplierContracts.length > 0 && (
                      <datalist id="supplier-contracts-list">
                        {supplierContracts.map((c, i) => <option key={i} value={c.contract_number} label={`${c.contract_number} — ${c.description || ''}`} />)}
                      </datalist>
                    )}
                  </div>
                ) : <p className="h-8 flex items-center text-sm">{order.contract_number||'—'}</p>}</div>
              <div className="space-y-1"><p className="text-xs font-medium text-muted-foreground">Order Carbon</p>
                <p className="h-8 flex items-center text-sm">{(order.carbon_emissions||0).toLocaleString()} CO2e</p></div>
            </div>
            {canEdit && (
              <div className="flex justify-start gap-2 mt-3 pt-2 border-t">
                <Button size="sm" onClick={saveHeader} disabled={saving}><Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save Header'}</Button>
                {canSendFromHeader && (
                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={openHeaderSend}>
                    <ShoppingCart className="h-4 w-4 mr-1" /> Issue Order
                  </Button>
                )}
                {isIssued && (
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={openHeaderSend}>
                    <ShoppingCart className="h-4 w-4 mr-1" /> Re-issue Order
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Main content — sidebar + panel */}
      <div className="flex border rounded-lg bg-card min-h-[400px]">
        {/* Left sidebar */}
        <div className="flex flex-col border-r bg-muted/10 w-10 flex-shrink-0">
          {PO_PANELS.map(tab => {
            const isActive = activePanel === tab.id;
            const counts = order.counts || {};
            const count = tab.id === 'line-items' ? (counts.line_items || 0) :
                          tab.id === 'goods-received' ? (counts.goods_received || 0) :
                          tab.id === 'invoices' ? (counts.invoices || 0) :
                          tab.id === 'rule-approvers' ? (counts.waiting_approvers || 0) :
                          tab.id === 'cc-splits' ? (counts.cost_centre_splits || 0) :
                          tab.id === 'attachments' ? (counts.attachments || 0) :
                          tab.id === 'terms' ? (counts.terms_conditions || 0) :
                          tab.id === 'notes' ? (counts.notes || 0) :
                          tab.id === 'events' ? (counts.events || 0) : 0;
            return (
              <button key={tab.id} onClick={() => setActivePanel(tab.id)} title={tab.label}
                className={`relative flex items-center justify-center h-10 w-10 transition-colors border-b ${isActive ? 'bg-primary/10 border-l-2 border-l-primary' : 'hover:bg-accent'}`}>
                <tab.icon className={`h-4 w-4 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                {count > 0 && <span className="absolute top-0.5 right-0.5 min-w-[14px] h-3.5 rounded-full bg-primary text-white text-[9px] flex items-center justify-center px-0.5">{count}</span>}
              </button>
            );
          })}
        </div>

        {/* Panel content */}
        <div className="flex-1 p-4 overflow-y-auto">
          {/* Line Items */}
          {activePanel === 'line-items' && (
            <POLineItemsPanel order={order} orderNumber={orderNumber} canEdit={canEdit} fmtVal={fmtVal} fmtTotal={fmtTotal} currency={currency} onReload={load} mandatoryLineFields={mandatoryFields.line || []} onSaveHeader={saveHeader} />
          )}

          {/* Approval Rules & Approvers */}
          {activePanel === 'rule' && <POApprovalRulesPanel order={order} orderNumber={orderNumber} />}
          {activePanel === 'rule-approvers' && <POApproversPanel order={order} orderNumber={orderNumber} user={user} canEdit={canEdit} onReload={load} fmtTz={fmtTz} />}
          {activePanel === 'cc-splits' && (
            <CostCentreSplitsPanel docType="orders" docNumber={orderNumber} netTotal={order.net_total || order.total_value || 0} fmtTz={fmtTz} onUpdate={load} />
          )}
          {activePanel === 'cc-approval' && <div><h3 className="font-bold text-primary mb-3">Cost Centre Approval</h3><EmptyState title="No cost centre approvals" /></div>}
          {activePanel === 'group-approvers' && <div><h3 className="font-bold text-primary mb-3">Group Approvers</h3><EmptyState title="No group approvers" /></div>}
          {activePanel === 'attachments' && (
            <AttachmentsPanel docType="orders" docNumber={orderNumber} canEdit={true} onReload={load} fmtTz={fmtTz} />
          )}
          {activePanel === 'terms' && (
            <TermsPanel docType="orders" docNumber={orderNumber} canEdit={true} onReload={load} fmtTz={fmtTz} />
          )}
          {activePanel === 'notes' && (
            <NotesPanel docType="orders" docNumber={orderNumber} fmtTz={fmtTz} />
          )}

          {/* Goods Received */}
          {activePanel === 'goods-received' && (
            <GoodsReceivedPanel orderNumber={orderNumber} order={order} fmtTz={fmtTz} fmtTotal={fmtTotal} onReload={load} />
          )}

          {/* Invoices */}
          {activePanel === 'invoices' && (
            <POInvoicesPanel orderNumber={orderNumber} currency={currency} fmtTz={fmtTz} />
          )}

          {/* Event Log */}
          {activePanel === 'events' && (
            <div>
              <h3 className="font-bold text-primary mb-3">Document Audit Trail</h3>
              {events.length === 0 ? <EmptyState title="No events yet" /> : (
                <table className="w-full text-xs">
                  <thead><tr className="bg-muted/50 border-b">
                    <SortableTableHeader label="Date & Time" field="event_date" sortState={poEvtSort} onSort={handlePoEvtSort} />
                    <SortableTableHeader label="User" field="user" sortState={poEvtSort} onSort={handlePoEvtSort} />
                    <SortableTableHeader label="Event" field="event" sortState={poEvtSort} onSort={handlePoEvtSort} />
                    <SortableTableHeader label="IP Address" field="ip_address" sortState={poEvtSort} onSort={handlePoEvtSort} className="text-right" />
                  </tr></thead>
                  <tbody>
                    {sortData(events, poEvtSort.field, poEvtSort.dir).map((e, i) => (
                      <tr key={i} className="border-b hover:bg-accent/20">
                        <td className="px-3 py-2 text-muted-foreground whitespace-nowrap font-mono">{fmtTz(e.event_date)}</td>
                        <td className="px-3 py-2">{e.user}</td>
                        <td className="px-3 py-2">{e.event}</td>
                        <td className="px-3 py-2 text-right text-muted-foreground font-mono whitespace-nowrap">{e.ip_address || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Detail Popover */}
      {detailPopover && (
        <Dialog open onOpenChange={() => setDetailPopover(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="capitalize">{detailPopover} Details</DialogTitle></DialogHeader>
            <DetailInfoCard type={detailPopover} order={order} />
          </DialogContent>
        </Dialog>
      )}

      {/* Currency Change Dialog */}
      {currencyDialog && (
        <Dialog open onOpenChange={() => setCurrencyDialog(false)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Change Order Currency</DialogTitle></DialogHeader>
            <p className="text-xs text-muted-foreground mb-2">All line item prices will be converted at the current exchange rate.</p>
            <TypeAheadLookup endpoint="/api/data/currencies" valueField="code" labelField="description" value={newCurrency} displayValue={newCurrency} placeholder="Select currency..." onChange={code => setNewCurrency(code)} />
            <div className="flex justify-end gap-2 mt-3">
              <Button size="sm" onClick={changeCurrency}>Convert</Button>
              <Button size="sm" variant="outline" onClick={() => setCurrencyDialog(false)}>Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Cancel Order Dialog */}
      {showCancelDialog && (
        <Dialog open onOpenChange={() => { setShowCancelDialog(false); setCancelReason(''); }}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="text-red-600">Cancel Purchase Order</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">This action cannot be undone. Please provide a reason for cancellation.</p>
            <div className="space-y-2 mt-2">
              <Label className="text-xs text-red-500 font-semibold">Cancellation Reason *</Label>
              <Textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} rows={3} placeholder="Enter reason for cancellation..." className="text-sm" />
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => { setShowCancelDialog(false); setCancelReason(''); }}>Back</Button>
              <Button size="sm" variant="destructive" onClick={cancelOrder} disabled={!cancelReason.trim()}>Confirm Cancellation</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}



      {/* Send/Re-issue Order from Header */}
      {showHeaderSend && (
        <Dialog open onOpenChange={() => { if (!headerSending) setShowHeaderSend(false); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-700">
                <ShoppingCart className="h-5 w-5" />
                {isIssued ? 'Re-issue Purchase Order' : 'Issue Purchase Order'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="rounded-lg border bg-muted/30 p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Order</span><span className="font-bold text-primary">{orderNumber}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Supplier</span><span className="font-semibold">{order.supplier_name || '—'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Supplier Email</span>
                  {headerSupplierEmail ? <span className="font-medium text-green-700">{headerSupplierEmail}</span> : <span className="text-red-600 text-xs">No email on file</span>}
                </div>
                <div className="border-t pt-2 flex justify-between"><span className="text-muted-foreground">Items</span><span>{(order.line_items || []).length}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-bold text-primary">{currency} {(order.net_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></div>
                {isIssued && order.version > 1 && <div className="flex justify-between"><span className="text-muted-foreground">Amendment</span><span className="text-amber-600 font-medium">{order.version - 1}</span></div>}
              </div>
              {!headerSupplierEmail && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                  <p className="text-sm text-red-700 font-semibold">Cannot send — no supplier email</p>
                </div>
              )}
              {isIssued && <p className="text-xs text-amber-600">This will resend the order to the supplier as an amended version.</p>}
              <div className="flex justify-end gap-2 pt-2">
                <Button size="sm" variant="outline" onClick={() => setShowHeaderSend(false)} disabled={headerSending}>Cancel</Button>
                <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={confirmHeaderSend}
                  disabled={headerSending || !headerSupplierEmail}>
                  {headerSending ? 'Issuing...' : isIssued ? 'Confirm & Re-issue' : 'Confirm & Issue'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Save as Template Dialog */}
      {showTemplateDialog && (
        <Dialog open onOpenChange={() => setShowTemplateDialog(false)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Save as Template</DialogTitle></DialogHeader>
            <p className="text-xs text-muted-foreground mb-2">Save this order as a reusable template for future orders.</p>
            <Input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder={`Template from ${orderNumber}`} className="mb-3" />
            <div className="flex justify-end gap-2">
              <Button size="sm" onClick={saveTemplate}>Save Template</Button>
              <Button size="sm" variant="outline" onClick={() => setShowTemplateDialog(false)}>Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Version History / External Order Dialog */}
      {showVersionHistory && (
        <Dialog open onOpenChange={() => setShowVersionHistory(false)}>
          <DialogContent className="max-w-2xl" data-testid="version-history-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                External Order — Version History
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {/* Live current view link */}
              <div className="flex items-center justify-between p-3 border rounded-lg bg-accent/30">
                <div>
                  <p className="text-sm font-semibold">Current Order (Live View)</p>
                  <p className="text-xs text-muted-foreground">
                    {(order.version || 1) > 1 ? `Amendment ${order.version - 1}` : 'Original Issue'}
                  </p>
                </div>
                <Button size="sm" variant="outline" data-testid="view-live-external-order" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderNumber}/report/html?internal=false`, '_blank')}>
                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> View
                </Button>
              </div>

              {loadingVersions ? (
                <div className="py-8 text-center text-sm text-muted-foreground">Loading version history...</div>
              ) : versionData?.versions?.length > 1 || ((order.version || 1) > 1 && versionData?.versions?.length > 0) ? (
                <>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Previous Versions Sent to Supplier</p>
                  <div className="border rounded-lg divide-y max-h-[350px] overflow-y-auto">
                    {versionData.versions.map(v => (
                      <div key={v.version} className="flex items-center justify-between px-4 py-3 hover:bg-accent/20 transition-colors" data-testid={`version-row-${v.version}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px]">
                              {v.version === 1 ? 'Original' : `Amendment ${v.version - 1}`}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Issued {v.issued_date ? new Date(v.issued_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: tz }) : '—'}
                            {v.issued_by ? ` by ${v.issued_by}` : ''}
                          </p>
                          {v.amendment_notes && (
                            <p className="text-xs text-amber-700 mt-0.5">Reason: {v.amendment_notes}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 ml-3">
                          <Button size="sm" variant="outline" className="h-7 px-2" data-testid={`view-pdf-v${v.version}`}
                            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderNumber}/versions/${v.version}/pdf`, '_blank')}>
                            <Eye className="h-3.5 w-3.5 mr-1" /> View
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2" data-testid={`download-pdf-v${v.version}`}
                            onClick={() => {
                              const a = document.createElement('a');
                              a.href = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderNumber}/versions/${v.version}/pdf`;
                              a.download = `Order_${orderNumber}_v${v.version}.pdf`;
                              a.click();
                            }}>
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="py-4 text-center text-sm text-muted-foreground border rounded-lg">
                  <p>No previous versions — this order has not been amended</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}


    </AppShell>
  );
}

